/**
 * Real client for docs/api-contract.md. Same origin as the page (`VITE_API_BASE` empty in production);
 * who is calling = the HttpOnly session cookie set by the POS sign-in (GET /auth/sso). A 401 anywhere
 * calls the handler from `onUnauthorized` (the session store shows the sign-in screen).
 */
import type { ChatApi, UploadProgress } from './client'
import { ApiError } from './client'
import { noteServerDate } from '../lib/serverTime'
import type {
  AiConfig,
  AiDraftResponse,
  AiIntent,
  AiSettings,
  Bootstrap,
  CannedInput,
  CannedItem,
  CannedList,
  ConversationDetail,
  ConversationPage,
  ConversationPatch,
  ConversationSummary,
  LearnAction,
  LearnItem,
  ListQuery,
  MediaAsset,
  MediaList,
  MediaPatch,
  MediaQuery,
  MediaRegister,
  MediaUploadRequest,
  MediaUploadTicket,
  MessagePage,
  OrderDraft,
  SearchResult,
  SendMessageBody,
  SendResult,
  UploadTarget,
  WebchatSettings,
  WebchatSettingsResponse,
  WebOrders,
  WebVisitorInfo,
} from './types'

type Query = Record<string, string | number | null | undefined>

let unauthorized: (() => void) | null = null

/** Called on every 401 response (session expired, signed out in another tab, staff disabled). */
export function onUnauthorized(fn: (() => void) | null): void {
  unauthorized = fn
}

/** Other clients on the same session (api/pos/http.ts) report their 401s here too. */
export function reportUnauthorized(): void {
  unauthorized?.()
}

export class HttpChatApi implements ChatApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private url(path: string, q?: Query): string {
    const u = new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin)
    for (const [k, v] of Object.entries(q ?? {})) {
      if (v !== null && v !== undefined && v !== '') u.searchParams.set(k, String(v))
    }
    return u.toString()
  }

  private async req<T>(
    method: string,
    path: string,
    opts: { query?: Query; body?: unknown; timeoutMs?: number } = {},
  ): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const sentAt = Date.now()
      const res = await this.fetchImpl(this.url(path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      // the server's clock (lib/serverTime.ts): due / overdue times follow it — not from a cached answer (Age)
      if (!res.headers?.get?.('Age')) noteServerDate(res.headers?.get?.('Date'), sentAt, Date.now())
      if (res.status === 401) unauthorized?.()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      if (res.status === 204) return undefined as T
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  bootstrap(): Promise<Bootstrap> {
    return this.req('GET', '/bootstrap')
  }

  listConversations(q: ListQuery): Promise<ConversationPage> {
    return this.req('GET', '/conversations', {
      query: { tab: q.tab, channel: q.channel, scope: q.scope, cursor: q.cursor, limit: q.limit, tag: q.tag },
    })
  }

  getConversation(id: string): Promise<ConversationDetail> {
    return this.req('GET', `/conversations/${encodeURIComponent(id)}`)
  }

  listMessages(id: string, opts: { before?: string | null; limit?: number } = {}): Promise<MessagePage> {
    return this.req('GET', `/conversations/${encodeURIComponent(id)}/messages`, {
      query: { before: opts.before, limit: opts.limit ?? 50 },
    })
  }

  search(q: string): Promise<SearchResult> {
    return this.req('GET', '/search', { query: { q } })
  }

  markRead(id: string): Promise<void> {
    return this.req('POST', `/conversations/${encodeURIComponent(id)}/read`)
  }

  sendMessage(id: string, body: SendMessageBody): Promise<SendResult> {
    return this.req('POST', `/conversations/${encodeURIComponent(id)}/messages`, { body })
  }

  retryMessage(messageId: string): Promise<SendResult> {
    return this.req('POST', `/messages/${encodeURIComponent(messageId)}/retry`)
  }

  learnAction(messageId: string, action: LearnAction, text?: string): Promise<{ item: LearnItem }> {
    const body = action === 'approve' && text ? { text } : undefined
    return this.req('POST', `/ai/learn/${encodeURIComponent(messageId)}/${action}`, body ? { body } : {})
  }

  learnEdit(messageId: string, text: string): Promise<{ item: LearnItem }> {
    return this.req('PATCH', `/ai/learn/${encodeURIComponent(messageId)}`, { body: { text } })
  }

  updateConversation(id: string, patch: ConversationPatch): Promise<ConversationSummary> {
    return this.req('PATCH', `/conversations/${encodeURIComponent(id)}`, { body: patch })
  }

  setAvailability(available: boolean): Promise<void> {
    return this.req('PUT', '/me/availability', { body: { available } })
  }

  /** Not in API v1 — POS order creation is not decided yet. */
  async getOrderDraft(): Promise<OrderDraft | null> {
    return null
  }

  getDraft(id: string, opts: { refresh?: boolean } = {}): Promise<AiDraftResponse> {
    return this.req('GET', `/conversations/${encodeURIComponent(id)}/draft`, {
      query: { refresh: opts.refresh ? 1 : null },
    })
  }

  /** Usually 1–3 s; a full AI draft can take up to ~40 s on a slow model, so wait 45 s before giving up. */
  requestDraft(id: string, opts: { refresh?: boolean; suggestionId?: string | null; adapt?: boolean; nextStep?: boolean } = {}): Promise<AiDraftResponse> {
    // "ให้ AI พาไปขั้นต่อไป": `next_step` alone (the API reads the chat's step from its script)
    const sg = opts.nextStep
      ? { next_step: true }
      : opts.suggestionId
        ? { suggestion_id: opts.suggestionId, ...(opts.adapt ? { adapt: true } : {}) }
        : {}
    return this.req('POST', `/conversations/${encodeURIComponent(id)}/draft`, {
      body: { refresh: !!opts.refresh, ...sg },
      timeoutMs: 45000,
    })
  }

  async refreshProfile(contactId: string): Promise<void> {
    await this.req('POST', `/contacts/${encodeURIComponent(contactId)}/refresh-profile`)
  }

  getAiConfig(): Promise<AiConfig> {
    return this.req('GET', '/ai/config')
  }

  updateAiSettings(patch: Partial<AiSettings>): Promise<AiSettings> {
    return this.req('PUT', '/ai/settings', { body: patch })
  }

  updateSlipSettings(patch: { auto: boolean }): Promise<{ auto: boolean }> {
    return this.req('PUT', '/slips/settings', { body: patch })
  }

  /** The POS asks the slip provider (up to ~20 s): wait longer than a normal call. */
  checkSlip(messageId: string): Promise<SendResult> {
    return this.req('POST', `/messages/${encodeURIComponent(messageId)}/slip-check`, { timeoutMs: 60000 })
  }

  async getWebVisitor(id: string): Promise<WebVisitorInfo | null> {
    try {
      return await this.req<WebVisitorInfo>('GET', `/conversations/${encodeURIComponent(id)}/web`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  async getWebOrders(id: string, fresh = false): Promise<WebOrders | null> {
    try {
      return await this.req<WebOrders>('GET', `/conversations/${encodeURIComponent(id)}/web/orders${fresh ? '?fresh=1' : ''}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  sendTyping(id: string): Promise<void> {
    return this.req('POST', `/conversations/${encodeURIComponent(id)}/typing`)
  }

  getWebchatSettings(): Promise<WebchatSettingsResponse> {
    return this.req('GET', '/webchat/settings')
  }

  updateWebchatSettings(patch: Partial<WebchatSettings>): Promise<WebchatSettingsResponse> {
    return this.req('PUT', '/webchat/settings', { body: patch })
  }

  // ------------------------------------------------ library (docs/library.md)

  listMedia(q: MediaQuery = {}): Promise<MediaList> {
    return this.req('GET', '/media', { query: { folder: q.folder ?? undefined, q: q.q, kind: q.kind } })
  }

  createMediaUpload(req: MediaUploadRequest): Promise<MediaUploadTicket> {
    return this.req('POST', '/media/uploads', { body: req })
  }

  putMediaObject(
    target: UploadTarget & { multipart?: boolean; part_bytes?: number },
    blob: Blob,
    opts: { onProgress?: UploadProgress; signal?: AbortSignal } = {},
  ): Promise<void> {
    return target.multipart ? putMultipart(target, blob, opts) : putOnce(target, blob, opts)
  }

  registerMedia(body: MediaRegister): Promise<MediaAsset> {
    return this.req('POST', '/media', { body })
  }

  updateMedia(id: string, patch: MediaPatch): Promise<MediaAsset> {
    return this.req('PATCH', `/media/${encodeURIComponent(id)}`, { body: patch })
  }

  deleteMedia(id: string): Promise<void> {
    return this.req('DELETE', `/media/${encodeURIComponent(id)}`)
  }

  listCanned(opts: { conversationId?: string | null } = {}): Promise<CannedList> {
    return this.req('GET', '/canned', { query: { conversation_id: opts.conversationId } })
  }

  createCanned(body: CannedInput): Promise<CannedItem> {
    return this.req('POST', '/canned', { body })
  }

  updateCanned(id: string, patch: CannedInput): Promise<CannedItem> {
    return this.req('PATCH', `/canned/${encodeURIComponent(id)}`, { body: patch })
  }

  deleteCanned(id: string): Promise<void> {
    return this.req('DELETE', `/canned/${encodeURIComponent(id)}`)
  }

  reorderCanned(ids: string[]): Promise<void> {
    return this.req('PUT', '/canned/order', { body: { ids } })
  }

  async listIntents(): Promise<AiIntent[]> {
    return (await this.req<{ items: AiIntent[] }>('GET', '/ai/intents')).items
  }
}

// ---------------------------------------------------------------- uploads straight to the Worker

/** Thai reason for a failed PUT to the media Worker. */
export function uploadErrorText(status: number): string {
  if (status === 0) return 'อัปโหลดไม่สำเร็จ เน็ตหลุดหรือเซิร์ฟเวอร์ไม่ตอบ ลองใหม่อีกครั้ง'
  if (status === 401) return 'สิทธิ์อัปโหลดหมดเวลา ลองอัปโหลดใหม่'
  if (status === 403) return 'หน้านี้ยังไม่ได้รับอนุญาตให้อัปโหลด แจ้งผู้ดูแลระบบ'
  if (status === 413) return 'ไฟล์ใหญ่เกินที่ตั้งไว้'
  if (status === 415) return 'ชนิดไฟล์ไม่ตรงกับที่ขออัปโหลด'
  if (status === 503) return 'คลังรูปยังไม่พร้อม แจ้งผู้ดูแลระบบ'
  return `อัปโหลดไม่สำเร็จ (${status})`
}

const ABORTED = 'ยกเลิกการอัปโหลดแล้ว'

/** One PUT with upload progress (fetch has no upload progress, XHR does). No cookies: the ticket is the auth.
 *  A signal aborted before the call never sends anything ('abort' does not fire again for it). */
function xhrPut(url: string, ticket: string, blob: Blob, contentType: string | null,
                onProgress: ((sent: number) => void) | undefined, signal: AbortSignal | undefined): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError(0, ABORTED))
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.withCredentials = false
    xhr.setRequestHeader('X-Media-Ticket', ticket)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText)
      else reject(new ApiError(xhr.status, uploadErrorText(xhr.status)))
    }
    xhr.onerror = () => reject(new ApiError(0, uploadErrorText(0)))
    xhr.onabort = () => reject(new ApiError(0, ABORTED))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(blob)
  })
}

async function putOnce(target: UploadTarget, blob: Blob,
                       opts: { onProgress?: UploadProgress; signal?: AbortSignal }): Promise<void> {
  await xhrPut(target.url, target.ticket, blob, blob.type || 'application/octet-stream',
    (sent) => opts.onProgress?.(sent, blob.size), opts.signal)
  opts.onProgress?.(blob.size, blob.size)
}

async function mpuCall<T>(url: string, ticket: string, method: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, {
      method,
      credentials: 'omit',
      headers: { 'X-Media-Ticket': ticket, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new ApiError(0, uploadErrorText(0))
  }
  if (!res.ok) throw new ApiError(res.status, uploadErrorText(res.status))
  return (await res.json()) as T
}

/** Files over the Worker's request limit: create → parts (same size, last smaller) → complete. */
async function putMultipart(target: UploadTarget & { part_bytes?: number }, blob: Blob,
                            opts: { onProgress?: UploadProgress; signal?: AbortSignal }): Promise<void> {
  const size = target.part_bytes ?? 50 * 1024 * 1024
  if (opts.signal?.aborted) throw new ApiError(0, ABORTED) // no multipart upload is opened for a page already left
  const { uploadId } = await mpuCall<{ uploadId: string }>(`${target.url}/create`, target.ticket, 'POST')
  const q = `uploadId=${encodeURIComponent(uploadId)}`
  const parts: { partNumber: number; etag: string }[] = []
  try {
    for (let i = 0, n = 1; i < blob.size; i += size, n++) {
      const chunk = blob.slice(i, Math.min(i + size, blob.size))
      const text = await xhrPut(`${target.url}/part?${q}&part=${n}`, target.ticket, chunk, null,
        (sent) => opts.onProgress?.(i + sent, blob.size), opts.signal)
      const r = JSON.parse(text) as { etag: string }
      parts.push({ partNumber: n, etag: r.etag })
    }
    await mpuCall(`${target.url}/complete?${q}`, target.ticket, 'POST', { parts })
    opts.onProgress?.(blob.size, blob.size)
  } catch (e) {
    void mpuCall(`${target.url}?${q}`, target.ticket, 'DELETE').catch(() => undefined)
    throw e
  }
}
